import type { InvoiceDetailResponse, InvoicePart } from '../types/invoice';
import type { InvoicingSettings, LkqDiscount, LkqMarkup } from '../hooks/useGetSettings';
import { LKQ_PRICING_WILDCARD } from './lkqPricingConstants';

const USED_AND_AFTERMARKET_CIECA = new Set(['PAL', 'PAA']);

export function isUsedOrAftermarketCiecaCode(ciecaCode: string | null | undefined): boolean {
    return USED_AND_AFTERMARKET_CIECA.has((ciecaCode ?? '').trim().toUpperCase());
}

function roundMoney(value: number): number {
    return Math.round(value * 100) / 100;
}

function idKey(value: string | null | undefined): string | null {
    if (value == null) return null;
    const s = String(value).trim();
    return s.length > 0 ? s : null;
}

function normalizeState(state: string | null | undefined): string | null {
    const s = (state ?? '').trim().toUpperCase();
    if (s === LKQ_PRICING_WILDCARD) return LKQ_PRICING_WILDCARD;
    return /^[A-Z]{2}$/.test(s) ? s : null;
}

function dimensionKey(value: string | null | undefined): string {
    return idKey(value) ?? LKQ_PRICING_WILDCARD;
}

function dimensionMatches(rowValue: string, invoiceValue: string | null): boolean {
    if (rowValue === LKQ_PRICING_WILDCARD) return true;
    if (!invoiceValue) return false;
    return rowValue === invoiceValue;
}

type PricingRow = {
    insuranceCompanyId: string;
    supplierId?: string;
    state: string;
    partKind?: string;
    make?: string;
};

function normalizePartKind(value: string | null | undefined): string | null {
    const s = (value ?? '').trim().toLowerCase();
    if (!s) return null;
    if (s === LKQ_PRICING_WILDCARD) return LKQ_PRICING_WILDCARD;
    if (s === 'used' || s === 'aftermarket' || s === 'oem' || s === 'glass' || s === 'reman') return s;
    return null;
}

function normalizeMake(value: string | null | undefined): string | null {
    const s = (value ?? '').trim().toLowerCase();
    if (!s) return null;
    if (s === LKQ_PRICING_WILDCARD) return LKQ_PRICING_WILDCARD;
    return s;
}

/** Same part-kind rule as invoice processing. A blank row kind is legacy used + aftermarket. */
function partKindMatches(rowKind: string | null | undefined, lineKind: string | null | undefined): boolean {
    if (lineKind == null || String(lineKind).trim() === '') return true;
    const line = normalizePartKind(lineKind);
    if (!line || line === LKQ_PRICING_WILDCARD) return false;
    const row = normalizePartKind(rowKind);
    if (row === LKQ_PRICING_WILDCARD) return true;
    if (!row) return line === 'used' || line === 'aftermarket';
    return row === line;
}

/** A blank row make matches any vehicle. An explicit make matches only that make. */
function makeMatches(rowMake: string | null | undefined, lineMake: string | null | undefined): boolean {
    const row = normalizeMake(rowMake) ?? LKQ_PRICING_WILDCARD;
    if (row === LKQ_PRICING_WILDCARD) return true;
    const line = normalizeMake(lineMake);
    if (!line || line === LKQ_PRICING_WILDCARD) return false;
    return row === line;
}

function rowSpecificity(row: PricingRow): number {
    let score = 0;
    if (dimensionKey(row.insuranceCompanyId) !== LKQ_PRICING_WILDCARD) score += 1;
    if (dimensionKey(row.supplierId) !== LKQ_PRICING_WILDCARD) score += 1;
    if ((normalizeState(row.state) ?? LKQ_PRICING_WILDCARD) !== LKQ_PRICING_WILDCARD) score += 1;
    const kind = normalizePartKind(row.partKind);
    if (kind && kind !== LKQ_PRICING_WILDCARD) score += 2;
    else if (!kind) score += 1;
    const make = normalizeMake(row.make);
    if (make && make !== LKQ_PRICING_WILDCARD) score += 1;
    return score;
}

function matchPricingRow<T extends PricingRow>(
    rows: T[] | undefined,
    invoice: {
        insuranceCompanyId?: string | null;
        supplierId?: string | null;
        state?: string | null;
        partKind?: string | null;
        make?: string | null;
    },
): T | undefined {
    if (!rows?.length) return undefined;
    const insuranceKey = idKey(invoice.insuranceCompanyId);
    const supplierKey = idKey(invoice.supplierId);
    const stateKey = normalizeState(invoice.state);
    const matches = rows.filter((row) => {
        const rowInsurance = dimensionKey(row.insuranceCompanyId);
        const rowSupplier = dimensionKey(row.supplierId);
        const rowState = normalizeState(row.state) ?? LKQ_PRICING_WILDCARD;
        return (
            dimensionMatches(rowInsurance, insuranceKey) &&
            dimensionMatches(rowSupplier, supplierKey) &&
            dimensionMatches(rowState, stateKey) &&
            partKindMatches(row.partKind, invoice.partKind) &&
            makeMatches(row.make, invoice.make)
        );
    });
    if (matches.length === 0) return undefined;
    return [...matches].sort((a, b) => rowSpecificity(b) - rowSpecificity(a))[0];
}

export type ManualMatchPartKind = 'used' | 'aftermarket' | 'oem' | 'glass' | 'reman';

/** Part kind for a matched Nexsyis line. Glass is recognized only from `partType`. */
export function pricingPartKindFromNexLine(line: {
    partType?: string | null;
    ciecaCode?: string | null;
} | null | undefined): ManualMatchPartKind | undefined {
    const raw = (line?.partType ?? '').trim().toLowerCase();
    if (raw.includes('glass')) return 'glass';
    if (raw.includes('aftermarket') || raw === 'a/m') return 'aftermarket';
    if (raw.includes('lkq') || raw.includes('recycl')) return 'used';
    if (raw.includes('reman')) return 'reman';
    if (raw.includes('oem')) return 'oem';

    const cieca = (line?.ciecaCode ?? '').trim().toUpperCase();
    if (cieca === 'PAA') return 'aftermarket';
    if (cieca === 'PAL') return 'used';
    if (cieca === 'PAN') return 'oem';
    if (cieca === 'PAM') return 'reman';
    return undefined;
}

export function resolveExtractedBuyForMarkup(part: Pick<InvoicePart, 'extractedUnitBuyPrice' | 'unitBuyPrice'>): number {
    if (part.extractedUnitBuyPrice != null && Number.isFinite(part.extractedUnitBuyPrice)) {
        return Math.abs(Number(part.extractedUnitBuyPrice));
    }
    return Math.abs(Number(part.unitBuyPrice ?? 0));
}

export function resolveExtractedListForMarkup(
    part: Pick<InvoicePart, 'extractedListPrice'>,
): number | null {
    if (part.extractedListPrice != null && Number.isFinite(part.extractedListPrice)) {
        const list = Math.abs(Number(part.extractedListPrice));
        return list > 0 ? list : null;
    }
    return null;
}

export type ManualMatchLkqListPriceContext = {
    invoiceType?: InvoiceDetailResponse['invoiceType'] | string | null;
    /** Persisted markup snapshot from last org pricing pass (optional). */
    lkqListPriceMarkup?: InvoiceDetailResponse['lkqListPriceMarkup'];
    lkqListPriceDiscount?: InvoiceDetailResponse['lkqListPriceDiscount'];
    invoicingSettings?: Pick<
        InvoicingSettings,
        | 'defaultLkqMarkupPercentage'
        | 'preferInvoiceListPriceOverLkqMarkup'
        | 'lkqMarkups'
        | 'lkqDiscounts'
    > | null;
    insuranceCompanyId?: string | null;
    supplierId?: string | null;
    /** Shop location state (AA), when known. */
    locationState?: string | null;
    /** Part kind of the Nexsyis line just matched. */
    partKind?: ManualMatchPartKind | null;
    /** Vehicle make from the purchase-order VIN. */
    vehicleMake?: string | null;
};

export type ManualMatchLkqMarkupResult = {
    mode: 'markup';
    listPrice: number;
    markupPercentage: number;
    source: 'settings_row' | 'default';
    /** True when invoice list was kept instead of applying markup. */
    usedInvoiceListPrice: boolean;
    buyPrice: number;
    previousListPrice: number;
};

export type ManualMatchLkqDiscountResult = {
    mode: 'discount';
    /** Invoice rate kept as list. */
    listPrice: number;
    /** list × (1 − discount), applied immediately on match. */
    unitBuyPrice: number;
    discountPercentage: number;
    previousListPrice: number;
    previousUnitBuyPrice: number;
};

export type ManualMatchLkqBillAtCostResult = {
    mode: 'billAtCost';
    listPrice: number;
    unitBuyPrice: number;
    previousListPrice: number;
    previousUnitBuyPrice: number;
};

export type ManualMatchLkqListPriceResult =
    | ManualMatchLkqMarkupResult
    | ManualMatchLkqDiscountResult
    | ManualMatchLkqBillAtCostResult;

/** Same rule as `isTireLevyLine` in tireLevyPricing.ts: "tire" plus "levy" or "fee". */
function isTireLevyLine(part: { title?: string | null; partNumber?: string | null }): boolean {
    const text = `${part.title ?? ''} ${part.partNumber ?? ''}`;
    return /\btire\b/i.test(text) && /\b(?:levy|levies|fee|fees)\b/i.test(text);
}

function positiveMoney(value: number | null | undefined): number | null {
    if (value == null || !Number.isFinite(value)) return null;
    const amount = Math.abs(value);
    return amount >= 0.005 ? amount : null;
}

/** Same bill-at-cost amount as invoice processing. A blank buy uses the printed list. */
function tireLevyUnitAmount(part: Pick<InvoicePart, 'extractedListPrice' | 'extractedUnitBuyPrice' | 'listPrice' | 'unitBuyPrice'> & Partial<Pick<InvoicePart, 'totalBuyPrice' | 'qty'>>): number {
    const list = positiveMoney(part.extractedListPrice) ?? positiveMoney(part.listPrice);
    const buy = positiveMoney(part.extractedUnitBuyPrice) ?? positiveMoney(part.unitBuyPrice);
    if (buy != null) return buy;
    if (list != null) return list;
    const total = positiveMoney(part.totalBuyPrice);
    if (total == null) return 0;
    const qty = Math.abs(Number(part.qty ?? 1)) || 1;
    return total / qty;
}

function fmtOverrideMoney(value: number): string {
    return `$${value.toFixed(2)}`;
}

function formatMarkupPct(markupPercentage: number): string {
    return (markupPercentage * 100).toFixed(1);
}

/** Match-note reason text when a parts-specific markup changes list price. */
export function formatLkqMarkupMatchNoteReason(args: {
    buyPrice: number;
    markupPercentage: number;
    source: 'settings_row' | 'default';
}): string {
    const pct = formatMarkupPct(args.markupPercentage);
    const buy = fmtOverrideMoney(args.buyPrice);
    if (args.source === 'default') {
        return `Parts-specific pricing rule: list price set to buy (${buy}) plus ${pct}% markup per your default parts-specific markup setting.`;
    }
    return `Parts-specific pricing rule: list price set to buy (${buy}) plus ${pct}% markup per your Parts-specific and sublet-specific markups.`;
}

function resolveExtractedListForDiscount(
    part: Pick<InvoicePart, 'extractedListPrice' | 'extractedUnitBuyPrice' | 'listPrice' | 'unitBuyPrice'>,
): number {
    const hasSnapshot = part.extractedListPrice != null || part.extractedUnitBuyPrice != null;
    const list = Math.abs(Number((hasSnapshot ? part.extractedListPrice : part.listPrice) ?? 0));
    const buy = Math.abs(Number((hasSnapshot ? part.extractedUnitBuyPrice : part.unitBuyPrice) ?? 0));
    if (list > 0 && buy > 0) return Math.max(list, buy);
    if (list > 0) return list;
    return buy;
}

/**
 * Resolve prices after a manual Nexsyis match.
 * A discount row sets buy to list × (1 − discount) immediately.
 * Otherwise a markup row sets list from buy, the same way invoice processing does.
 */
export function resolveListPriceAfterManualLkqMatch(
    part: Pick<InvoicePart, 'extractedListPrice' | 'extractedUnitBuyPrice' | 'unitBuyPrice' | 'listPrice'> & Partial<Pick<InvoicePart, 'title' | 'partNumber' | 'totalBuyPrice' | 'qty'>>,
    ciecaCode: string | null | undefined,
    ctx: ManualMatchLkqListPriceContext,
): ManualMatchLkqListPriceResult | null {
    if (isTireLevyLine(part)) {
        const amount = roundMoney(tireLevyUnitAmount(part));
        return {
            mode: 'billAtCost',
            listPrice: amount,
            unitBuyPrice: amount,
            previousListPrice: roundMoney(Math.abs(Number(part.listPrice ?? 0))),
            previousUnitBuyPrice: roundMoney(Math.abs(Number(part.unitBuyPrice ?? 0))),
        };
    }
    if ((ctx.invoiceType ?? 'received') !== 'received') return null;
    const partKind = ctx.partKind ?? pricingPartKindFromNexLine({ ciecaCode });
    if (!partKind) return null;

    const pricingKeys = {
        insuranceCompanyId: ctx.insuranceCompanyId,
        supplierId: ctx.supplierId,
        state: ctx.locationState,
        partKind,
        make: ctx.vehicleMake,
    };

    const discountRow = matchPricingRow(ctx.invoicingSettings?.lkqDiscounts as LkqDiscount[] | undefined, pricingKeys);
    if (
        discountRow &&
        Number.isFinite(discountRow.discountPercentage) &&
        discountRow.discountPercentage >= 0 &&
        discountRow.discountPercentage <= 1
    ) {
        const list = roundMoney(resolveExtractedListForDiscount(part));
        const previousListPrice = roundMoney(Math.abs(Number(part.listPrice ?? list)));
        const previousUnitBuyPrice = roundMoney(Math.abs(Number(part.unitBuyPrice ?? 0)));
        return {
            mode: 'discount',
            listPrice: list,
            unitBuyPrice: roundMoney(list * (1 - discountRow.discountPercentage)),
            discountPercentage: discountRow.discountPercentage,
            previousListPrice,
            previousUnitBuyPrice,
        };
    }

    let markupPercentage: number | null = null;
    let source: 'settings_row' | 'default' | null = null;
    const markupRow = matchPricingRow(ctx.invoicingSettings?.lkqMarkups as LkqMarkup[] | undefined, pricingKeys);
    if (markupRow && Number.isFinite(markupRow.markupPercentage) && markupRow.markupPercentage >= 0) {
        markupPercentage = markupRow.markupPercentage;
        source = 'settings_row';
    } else if (partKind === 'used' || partKind === 'aftermarket') {
        const fallback = ctx.invoicingSettings?.defaultLkqMarkupPercentage;
        if (fallback != null && Number.isFinite(fallback) && fallback >= 0) {
            markupPercentage = fallback;
            source = 'default';
        }
    }

    if (markupPercentage == null || source == null) return null;

    const buy = resolveExtractedBuyForMarkup(part);
    const previousListPrice = roundMoney(
        part.extractedListPrice != null && Number.isFinite(part.extractedListPrice)
            ? Math.abs(Number(part.extractedListPrice))
            : Math.abs(Number(part.listPrice ?? 0)),
    );

    const preferInvoiceList = ctx.invoicingSettings?.preferInvoiceListPriceOverLkqMarkup === true;
    if (preferInvoiceList) {
        const invoiceList = resolveExtractedListForMarkup(part);
        if (invoiceList != null) {
            return {
                mode: 'markup',
                listPrice: roundMoney(invoiceList),
                markupPercentage,
                source,
                usedInvoiceListPrice: true,
                buyPrice: buy,
                previousListPrice,
            };
        }
    }

    return {
        mode: 'markup',
        listPrice: roundMoney(buy * (1 + markupPercentage)),
        markupPercentage,
        source,
        usedInvoiceListPrice: false,
        buyPrice: buy,
        previousListPrice,
    };
}

/**
 * List price after a manual Nexsyis match, or null when no markup row applies to this line.
 */
export function computeListPriceAfterManualLkqMatch(
    part: Pick<InvoicePart, 'extractedListPrice' | 'extractedUnitBuyPrice' | 'unitBuyPrice' | 'listPrice'>,
    ciecaCode: string | null | undefined,
    ctx: ManualMatchLkqListPriceContext,
): number | null {
    return resolveListPriceAfterManualLkqMatch(part, ciecaCode, ctx)?.listPrice ?? null;
}

function formatLkqDiscountMatchNoteReason(args: { listPrice: number; discountPercentage: number }): string {
    const pct = formatMarkupPct(args.discountPercentage);
    const list = fmtOverrideMoney(args.listPrice);
    return `Parts-specific pricing rule: buy price set to list (${list}) minus ${pct}% discount per your Parts-specific and sublet-specific discounts.`;
}

/** Build a reconciliation match note when markup or discount changed the displayed price. */
export function buildManualMatchLkqListPriceNote(args: {
    partIndex: number;
    invoicePartNumber: string;
    nexPartNumber: string;
    result: ManualMatchLkqListPriceResult;
}): import('../types/invoice').ReconciliationMatchNote | null {
    if (args.result.mode === 'billAtCost') {
        const listChanged = Math.abs(args.result.listPrice - args.result.previousListPrice) >= 0.005;
        const buyChanged = Math.abs(args.result.unitBuyPrice - args.result.previousUnitBuyPrice) >= 0.005;
        if (!listChanged && !buyChanged) return null;
        return {
            partIndex: args.partIndex,
            invoicePartNumber: args.invoicePartNumber,
            nexPartNumber: args.nexPartNumber,
            reason: `Tire levy: list and buy set to ${fmtOverrideMoney(args.result.listPrice)}. Markups and discounts do not apply to this line.`,
            extractedFromPage: {
                listPrice: args.result.previousListPrice,
                unitBuyPrice: args.result.previousUnitBuyPrice,
            },
        };
    }
    if (args.result.mode === 'discount') {
        const listChanged = Math.abs(args.result.listPrice - args.result.previousListPrice) >= 0.005;
        const buyChanged = Math.abs(args.result.unitBuyPrice - args.result.previousUnitBuyPrice) >= 0.005;
        if (!listChanged && !buyChanged) return null;
        return {
            partIndex: args.partIndex,
            invoicePartNumber: args.invoicePartNumber,
            nexPartNumber: args.nexPartNumber,
            reason: formatLkqDiscountMatchNoteReason({
                listPrice: args.result.listPrice,
                discountPercentage: args.result.discountPercentage,
            }),
            extractedFromPage: {
                listPrice: args.result.previousListPrice,
                unitBuyPrice: args.result.previousUnitBuyPrice,
            },
        };
    }
    if (args.result.usedInvoiceListPrice) return null;
    if (Math.abs(args.result.listPrice - args.result.previousListPrice) < 0.005) return null;
    return {
        partIndex: args.partIndex,
        invoicePartNumber: args.invoicePartNumber,
        nexPartNumber: args.nexPartNumber,
        reason: formatLkqMarkupMatchNoteReason({
            buyPrice: args.result.buyPrice,
            markupPercentage: args.result.markupPercentage,
            source: args.result.source,
        }),
        extractedFromPage: {
            listPrice: args.result.previousListPrice,
            unitBuyPrice: args.result.buyPrice,
        },
    };
}

/**
 * Extracted buy to restore after unmatch, including an explicit 0.
 * List price is not a substitute. A blank extracted buy stays blank.
 */
export function computeBuyPriceAfterManualUnmatch(
    part: Pick<InvoicePart, 'extractedUnitBuyPrice'>,
): number | null {
    if (part.extractedUnitBuyPrice == null || !Number.isFinite(Number(part.extractedUnitBuyPrice))) {
        return null;
    }
    return roundMoney(Math.abs(Number(part.extractedUnitBuyPrice)));
}

/**
 * Buy to write on unmatch.
 * Markup only changes list, so those matches leave the buy alone.
 * A discount or tire levy restores the pre-match buy from the match note,
 * then the extracted buy, including an explicit 0.
 */
export function buyPriceAfterManualUnmatch(args: {
    part: Pick<InvoicePart, 'extractedUnitBuyPrice'>;
    matchChangedBuy: boolean;
    previousUnitBuyPrice?: number | null;
}): number | null {
    if (!args.matchChangedBuy) return null;
    const previous = args.previousUnitBuyPrice;
    if (previous != null && Number.isFinite(Number(previous))) {
        return roundMoney(Math.abs(Number(previous)));
    }
    return computeBuyPriceAfterManualUnmatch(args.part);
}

/** Restore extracted list price after unmatch (when a prior markup may have inflated list). */
export function computeListPriceAfterManualUnmatch(
    part: Pick<InvoicePart, 'extractedListPrice' | 'listPrice'>,
): number {
    if (part.extractedListPrice != null && Number.isFinite(part.extractedListPrice)) {
        return roundMoney(Math.abs(Number(part.extractedListPrice)));
    }
    return roundMoney(Math.abs(Number(part.listPrice ?? 0)));
}

/** Look up CIECA on a stored NEX PO line when the match payload omitted it. */
export function lookupCiecaCodeFromNexResponse(
    nexResponse: InvoiceDetailResponse['nexResponse'] | null | undefined,
    nexLine: {
        poHeaderID?: number | null;
        lineNumber?: number | null;
        unqSeq?: number | null;
        partNumber?: string | null;
    },
): string | undefined {
    if (!nexResponse?.length) return undefined;
    const poId = nexLine.poHeaderID != null ? Number(nexLine.poHeaderID) : null;
    const lineNumber = nexLine.lineNumber != null ? Number(nexLine.lineNumber) : null;
    if (poId == null || lineNumber == null || !Number.isFinite(poId) || !Number.isFinite(lineNumber)) {
        return undefined;
    }
    const unq = nexLine.unqSeq == null ? null : String(nexLine.unqSeq);
    for (const po of nexResponse) {
        for (const dl of po.detailLines ?? []) {
            const dlPo = Number(dl.poHeaderID ?? po.id);
            const dlLine = Number(dl.lineNumber);
            if (dlPo !== poId || dlLine !== lineNumber) continue;
            if (unq != null && String(dl.unqSeq ?? '') !== unq) continue;
            const code = typeof dl.ciecaCode === 'string' ? dl.ciecaCode.trim() : '';
            if (code) return code;
        }
    }
    return undefined;
}
